import React, { Component } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { externalMintFor, GATED_UNKNOWN, OWNER_ABI } from '../config/externalMints';

/**
 * ExternalMintPanel — what to show when minting happens somewhere else.
 *
 * Firmament and Way of the Worm are paused on this contract because they are
 * gated by something it cannot see. The modal used to render that as a greyed
 * "minting currently paused" button, which reads as "this project is over"
 * when it means "mint it over there".
 *
 * Where eligibility is answerable on chain it is answered here, for the wallet
 * that is actually connected: which Syncrets this account holds, and which of
 * them still carry an unspent Firmament claim. Where it is not -- Worm Disciple
 * status lives in a collection this contract knows nothing about -- the panel
 * says where the check happens instead of guessing.
 */
class ExternalMintPanel extends Component {

  constructor(props) {
    super(props);
    this.state = { checking: false, checked: false, held: [], eligible: [],
      allowed: false, sending: false, txHash: null, txError: null, error: null };
  }

  componentDidMount() { this.check(); this.checkProvenance(); }

  /**
   * Whether the contract registered as the artist belongs to the person who
   * posted the code.
   *
   * A gated project's artist address is a contract, and the details tab shows
   * it beside the address that posted the script -- two different addresses,
   * which read as a mismatch. They are not: the contract is owned by the
   * poster. Asked live, so the panel says what is true now rather than what
   * was true when this was written.
   */
  async checkProvenance() {
    const { contract, projectId } = this.props;
    const config = externalMintFor(projectId);
    if (!config || !config.provenance || !contract) return;
    const p = config.provenance;
    const owned = async (address) => {
      const c = new contract.constructor(OWNER_ABI, address);
      const o = await c.methods.owner().call();
      return String(o).toLowerCase() === p.codePoster.toLowerCase();
    };
    try {
      const artist = await owned(p.artistContract);
      const gate = p.gateContract ? await owned(p.gateContract) : null;
      this.setState({ provenance: { checked: true, artist, gate } });
    } catch (e) {
      this.setState({ provenance: { checked: true, error: e.message || String(e) } });
    }
  }

  renderProvenance() {
    const config = externalMintFor(this.props.projectId);
    if (!config || !config.provenance) return null;
    const p = config.provenance;
    const r = this.state.provenance;
    const short = (a) => a.slice(0, 6) + '…' + a.slice(-4);
    const style = { fontSize: '0.85em', opacity: 0.85, marginBottom: '0.75em' };
    if (!r || !r.checked) {
      return <p className="font-display" style={style}>
        The registered artist is a contract ({short(p.artistContract)}); checking who owns it…
      </p>;
    }
    if (r.error) {
      return <p className="font-display" style={style}>
        The registered artist is a contract ({short(p.artistContract)}), expected to be owned by the
        address that posted this project's code ({short(p.codePoster)}). Could not confirm that just now.
      </p>;
    }
    const ok = r.artist && (r.gate === null || r.gate);
    return <p className="font-display" style={style}>
      {ok ? '✓ ' : '⚠ '}
      The registered artist is a contract ({short(p.artistContract)})
      {r.artist ? ' owned by ' : ' NOT owned by '}
      {short(p.codePoster)}, the address that posted this project's code
      {p.gateContract ? (r.gate ? '; the mint contract is owned by the same address.' : '; the mint contract is owned by someone else.') : '.'}
      {ok ? ' The two addresses on the details tab are the same person.' : ''}
    </p>;
  }

  componentDidUpdate(prev) {
    if (prev.account !== this.props.account || prev.projectId !== this.props.projectId) {
      this.setState({ checked: false, held: [], eligible: [], allowed: false,
        txHash: null, txError: null, error: null }, () => this.check());
    }
  }

  /**
   * Which of the connected account's Syncrets can still claim.
   *
   * Reads holdings from the platform contract and the spent flag from the
   * gating contract. A claim is one-way, so an unspent answer is the only one
   * worth acting on.
   */
  async check() {
    const { account, contract, projectId } = this.props;
    const config = externalMintFor(projectId);
    if (!config || !config.gate || !account || !contract) return;
    if (this.state.checking || this.state.checked) return;

    // An allowlist gate answers directly: the contract is asked about this
    // wallet and that is the whole answer.
    if (config.gate.kind === 'allowlist') {
      this.setState({ checking: true });
      try {
        const gateContract = new contract.constructor(config.gate.abi, config.gate.contract);
        const allowed = await gateContract.methods[config.gate.check](account).call();
        this.setState({ checking: false, checked: true, allowed: allowed === true || allowed === 'true' });
      } catch (e) {
        this.setState({ checking: false, checked: true, error: e.message || String(e) });
      }
      return;
    }

    this.setState({ checking: true });
    try {
      const gate = config.gate;
      const tokenIds = await contract.methods.getTokenIdsForProjectId(gate.heldProjectId).call();

      // Ownership first, so the gating contract is only asked about tokens
      // this account actually holds.
      const owned = [];
      for (const tokenId of tokenIds) {
        const owner = await contract.methods.ownerOf(tokenId).call();
        if (String(owner).toLowerCase() === String(account).toLowerCase()) {
          const editionId = await contract.methods.tokenIdToEditionId(tokenId).call();
          owned.push({ tokenId: String(tokenId), editionId: String(editionId) });
        }
      }

      // App does not carry web3 into render, and threading it down just for
      // this would touch a lot of unrelated code. A web3 Contract instance's
      // own constructor is already bound to the connected provider, so a
      // second contract can be built from the one already in hand.
      const gateContract = new contract.constructor(gate.abi, gate.contract);
      const eligible = [];
      for (const t of owned) {
        try {
          const spent = await gateContract.methods.claimedToken(t.tokenId).call();
          if (!(spent === true || spent === 'true')) eligible.push(t);
        } catch (e) { /* unreadable for this token; leave it out rather than promise it */ }
      }

      this.setState({ checking: false, checked: true, held: owned, eligible });
    } catch (e) {
      this.setState({ checking: false, checked: true, error: e.message || String(e) });
    }
  }

  /**
   * Send the claim from here, so an eligible holder does not have to assemble
   * a raw call on Etherscan. The gating contract is the authority either way;
   * this only saves the trip.
   */
  async claim(arg) {
    const { account, contract, projectId } = this.props;
    const config = externalMintFor(projectId);
    if (!config || !config.claim || !account || !contract) return;

    this.setState({ sending: true, txError: null, txHash: null });
    try {
      const gateContract = new contract.constructor(config.gate.abi, config.gate.contract);
      const method = config.claim.argFrom
        ? gateContract.methods[config.claim.method](arg)
        : gateContract.methods[config.claim.method]();
      await method.send({ from: account })
        .on('transactionHash', (h) => this.setState({ txHash: h }));
      // The claim is spent now, so re-read rather than leaving a stale offer.
      this.setState({ sending: false, checked: false }, () => this.check());
    } catch (e) {
      const msg = (e && e.message) ? e.message : String(e);
      this.setState({
        sending: false,
        // A rejected signature is not a failure worth shouting about.
        txError: /User denied|user rejected/i.test(msg) ? null : msg.slice(0, 200),
      });
    }
  }

  renderTxState() {
    const { sending, txHash, txError } = this.state;
    if (sending && !txHash) {
      return <p className="font-display" style={{ fontSize: '0.9em' }}>
        <Spinner animation="grow" size="sm" />&nbsp; confirm in your wallet…
      </p>;
    }
    if (sending && txHash) {
      return <p className="font-display" style={{ fontSize: '0.9em' }}>
        <Spinner animation="grow" size="sm" />&nbsp; waiting for the transaction…
      </p>;
    }
    if (txError) {
      return <p className="font-display" style={{ fontSize: '0.85em' }}>
        The claim did not go through: {txError}
      </p>;
    }
    return null;
  }

  /** An allowlist gate: the contract said yes or no about this wallet. */
  renderAllowlist() {
    const { account, projectId } = this.props;
    const { checking, checked, allowed, error, sending } = this.state;
    const config = externalMintFor(projectId);

    if (!account) {
      return <p className="font-display" style={{ marginBottom: '1em' }}>
        Connect a wallet to check whether it can mint.
      </p>;
    }
    if (checking || !checked) {
      return <p className="font-display" style={{ marginBottom: '1em' }}>
        <Spinner animation="grow" size="sm" />&nbsp; asking the mint contract…
      </p>;
    }
    if (error) {
      return <p className="font-display" style={{ marginBottom: '1em' }}>
        Could not reach the mint contract just now.
      </p>;
    }
    // The button is always here, so the mint is visibly the thing this panel
    // is for. When the contract says no it is disabled with the reason beside
    // it, rather than vanishing and leaving nothing to explain the absence.
    return (
      <div style={{ marginBottom: '1em' }}>
        <p className="font-display" style={{ marginBottom: '0.6em' }}>
          {allowed
            ? <b>This wallet can mint.</b>
            : <span>The mint contract does not list this wallet as eligible.</span>}
        </p>
        <Button
          variant={allowed ? 'success btn-lg' : 'secondary btn-lg'}
          className="font-display"
          disabled={!allowed || sending}
          onClick={() => this.claim(null)}
        >
          {config.claim.verb}
        </Button>
        {this.renderTxState()}
      </div>
    );
  }

  renderEligibility() {
    const { account } = this.props;
    const { checking, checked, held, eligible, error } = this.state;

    if (!account) {
      return (
        <p className="font-display" style={{ marginBottom: '1em' }}>
          Connect a wallet to check whether it can claim.
        </p>
      );
    }
    if (checking || !checked) {
      return (
        <p className="font-display" style={{ marginBottom: '1em' }}>
          <Spinner animation="grow" size="sm" />&nbsp; checking this wallet…
        </p>
      );
    }
    if (error) {
      return (
        <p className="font-display" style={{ marginBottom: '1em' }}>
          Could not check eligibility just now. The claim contract is still the
          authority — check it directly.
        </p>
      );
    }
    if (eligible.length) {
      const { sending } = this.state;
      return (
        <div style={{ marginBottom: '1em' }}>
          <p className="font-display" style={{ marginBottom: '0.4em' }}>
            <b>This wallet can claim {eligible.length} Firmament.</b>
          </p>
          <p className="font-display" style={{ fontSize: '0.9em' }}>
            Unspent {eligible.length === 1 ? 'claim' : 'claims'} on Syncret{' '}
            {eligible.map(e => '#' + e.editionId).join(', ')}.
          </p>
          {/* One button per unspent claim: each is a separate Syncret and a
              separate transaction, so they are not collapsed into one. */}
          {eligible.map(e => (
            <Button
              key={e.tokenId}
              variant="success"
              className="font-display"
              style={{ margin: '0.25em' }}
              disabled={sending}
              onClick={() => this.claim(e.tokenId)}
            >
              claim with Syncret #{e.editionId}
            </Button>
          ))}
          {this.renderTxState()}
        </div>
      );
    }
    if (held.length) {
      return (
        <p className="font-display" style={{ marginBottom: '1em' }}>
          This wallet holds {held.length} Syncret{held.length === 1 ? '' : 's'}
          {' '}({held.map(h => '#' + h.editionId).join(', ')}), but{' '}
          {held.length === 1 ? 'its claim has' : 'all their claims have'} already
          been spent.
        </p>
      );
    }
    return (
      <p className="font-display" style={{ marginBottom: '1em' }}>
        This wallet holds no Syncret, so it has no claim.
      </p>
    );
  }

  /**
   * A gated project with no entry written for it yet. Says where to find the
   * rules rather than guessing at them.
   */
  renderUnknown() {
    return (
      <div className="external-mint">
        <p className="font-display" style={{ marginTop: '1em', marginBottom: '0.5em' }}>
          <b>{GATED_UNKNOWN.title}</b>
        </p>
        <p className="font-display" style={{ fontSize: '0.95em' }}>{GATED_UNKNOWN.summary}</p>
        <Button
          href={GATED_UNKNOWN.discordUrl}
          target="_new"
          rel="noopener noreferrer"
          variant="outline-primary btn-lg"
          className="font-display"
          style={{ marginBottom: '1em' }}
        >
          {GATED_UNKNOWN.discordLabel}
        </Button>
      </div>
    );
  }

  render() {
    const { projectId, gatedUnknown } = this.props;
    const config = externalMintFor(projectId);
    if (!config) return gatedUnknown ? this.renderUnknown() : null;

    return (
      <div className="external-mint">
        <p className="font-display" style={{ marginTop: '1em', marginBottom: '0.5em' }}>
          <b>{config.title}</b>
        </p>
        <p className="font-display" style={{ fontSize: '0.95em' }}>{config.summary}</p>
        {this.renderProvenance()}

        {config.gate
          ? (config.gate.kind === 'allowlist' ? this.renderAllowlist() : this.renderEligibility())
          : null}

        {/*
          Etherscan is the fallback, not the route. When the claim can be made
          from here it is made from here, and this stays as a small way to read
          the contract first -- so it is a quiet link rather than the biggest
          button on the panel, which is what it used to be.
        */}
        <div style={{ marginBottom: '1em' }}>
          <a
            href={config.mintUrl}
            target="_new"
            rel="noopener noreferrer"
            className="font-display"
            style={{ fontSize: '0.78em', opacity: 0.7 }}
          >
            {config.claim ? 'or make the call on Etherscan' : config.mintLabel}
          </a>
        </div>

        {config.howToQualify ? (
          <>
            <p className="font-display" style={{ fontSize: '0.85em', marginTop: '1em' }}>
              {config.howToQualify}
            </p>
            {config.qualifyUrl ? (
              <Button
                href={config.qualifyUrl}
                variant="outline-secondary"
                size="sm"
                className="font-display"
                onClick={() => { if (this.props.onNavigate) this.props.onNavigate(); }}
              >
                {config.qualifyLabel}
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
    );
  }
}

export default ExternalMintPanel;
